import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Alert, Button, Input, Select, Skeleton, Tooltip, Typography, theme } from 'antd';
import type { InputRef } from 'antd';
import { CloseOutlined, FullscreenExitOutlined, FullscreenOutlined, SearchOutlined } from '@ant-design/icons';
import { PlaybookBundle, PlaybookLang } from '../config/types';
import { tr } from '../config/strings';
import { anchorDomId } from '../config/anchor';
import { MIN_SEARCH_LENGTH, searchPlaybook } from '../config/search';
import { PlaybookParityError } from '../hooks/usePlaybookData';
import PlaybookContent from './PlaybookContent';
import styles from './PlaybookPanel.module.scss';

const PANEL_Z_INDEX = 950; // above page content and header, below antd Modal (1000)

interface Props {
  lang: PlaybookLang;
  onLangChange: (lang: PlaybookLang) => void;
  wide: boolean;
  onToggleWide: () => void;
  data?: PlaybookBundle;
  isLoading: boolean;
  error: Error | null;
  /** Section being read; any level. The panel shows its top-level section and scrolls to it. */
  viewingId: string;
  onViewingChange: (id: string) => void;
  highlightId?: string | null;
  onCopyLink: (id: string) => void;
  onClose: () => void;
  isMobile: boolean;
  isNarrow: boolean;
}

const PlaybookPanel = ({
  lang,
  onLangChange,
  wide,
  onToggleWide,
  data,
  isLoading,
  error,
  viewingId,
  onViewingChange,
  highlightId,
  onCopyLink,
  onClose,
  isMobile,
  isNarrow,
}: Props) => {
  const { token } = theme.useToken();
  const [keyword, setKeyword] = useState('');
  const searchRef = useRef<InputRef>(null);
  const bodyRef = useRef<HTMLDivElement>(null);
  const kw = keyword.trim();
  const searching = kw.length >= MIN_SEARCH_LENGTH;

  const doc = data?.[lang];
  const viewing = doc ? doc.byId[viewingId] ?? doc.sections[0] : undefined;
  const rootId = viewing?.rootId;
  const roots = useMemo(() => doc?.sections.filter((s) => s.level === 2) ?? [], [doc]);
  const subSections = useMemo(
    () => (doc && rootId ? doc.sections.filter((s) => s.rootId === rootId && s.level === 3) : []),
    [doc, rootId],
  );
  const rootTitle = roots.find((r) => r.id === rootId)?.title ?? '';
  const results = useMemo(() => (doc && searching ? searchPlaybook(doc, kw) : []), [doc, kw, searching]);

  useEffect(() => {
    searchRef.current?.focus();
  }, []);

  // Scroll to the requested section, or back to the top when only the top-level section changed.
  useEffect(() => {
    if (!viewing || searching) return;
    const timer = setTimeout(() => {
      if (viewing.level === 2) {
        bodyRef.current?.scrollTo({ top: 0 });
        return;
      }
      document.getElementById(anchorDomId(viewing.id))?.scrollIntoView({ block: 'start', behavior: 'smooth' });
    }, 60);
    return () => clearTimeout(timer);
  }, [viewing, searching, data]);

  const openSection = (id: string) => {
    setKeyword('');
    onViewingChange(id);
  };

  const navBody = (
    <nav aria-label={tr('navLabel', lang)} className={styles.nav}>
      {roots.map((root) => {
        const isViewing = root.id === rootId;
        return (
          <div key={root.id}>
            <button
              type='button'
              aria-current={isViewing ? 'true' : undefined}
              className={`${styles.navItem} ${isViewing ? styles.navItemActive : ''}`}
              onClick={() => openSection(root.id)}
            >
              {root.title}
            </button>
            {isViewing && !searching && subSections.length > 0 && (
              <div className={styles.subNav}>
                {subSections.map((sub) => (
                  <button
                    key={sub.id}
                    type='button'
                    className={`${styles.subItem} ${viewing?.id === sub.id ? styles.subItemActive : ''}`}
                    onClick={() => openSection(sub.id)}
                  >
                    {sub.title}
                  </button>
                ))}
              </div>
            )}
          </div>
        );
      })}
    </nav>
  );

  const renderBody = () => {
    if (isLoading || (!data && !error)) return <Skeleton active paragraph={{ rows: 8 }} />;

    if (error) {
      const details = error instanceof PlaybookParityError ? error.details : [];
      return (
        <Alert
          type='error'
          showIcon
          message={tr('loadFailed', lang)}
          description={
            details.length ? (
              <div>
                <div>{tr('invalidFiles', lang)}</div>
                <ul style={{ margin: '6px 0 0', paddingLeft: 18 }}>
                  {details.slice(0, 10).map((d) => (
                    <li key={d}>{d}</li>
                  ))}
                </ul>
              </div>
            ) : (
              error.message
            )
          }
        />
      );
    }

    if (searching) {
      if (!results.length) {
        return (
          <div style={{ color: token.colorTextTertiary, fontSize: 14, textAlign: 'center', padding: '20px 0' }}>
            {tr('noResults', lang)}
          </div>
        );
      }
      return (
        <>
          {results.map((r) => (
            <div
              key={r.section.id}
              role='button'
              tabIndex={0}
              onClick={() => openSection(r.section.id)}
              onKeyDown={(e) => e.key === 'Enter' && openSection(r.section.id)}
              className={styles.resultCard}
            >
              <div style={{ fontSize: 12, color: token.colorTextTertiary, fontWeight: 600 }}>{r.label}</div>
              <div style={{ fontSize: 14, marginTop: 4, lineHeight: 1.6 }}>
                {r.before}
                {r.hit && (
                  <mark style={{ background: token.colorWarningBg, color: 'inherit', padding: 0 }}>{r.hit}</mark>
                )}
                {r.after}
              </div>
            </div>
          ))}
        </>
      );
    }

    if (!doc || !rootId) return null;
    return (
      <PlaybookContent
        doc={doc}
        lang={lang}
        rootId={rootId}
        highlightId={highlightId}
        onNavigate={openSection}
        onCopyLink={onCopyLink}
      />
    );
  };

  const scrimStyle: React.CSSProperties = wide
    ? { position: 'fixed', inset: 0, zIndex: PANEL_Z_INDEX + 1, display: 'flex' }
    : {
        position: 'fixed',
        inset: 0,
        zIndex: PANEL_Z_INDEX,
        display: 'flex',
        alignItems: 'flex-end',
        justifyContent: 'flex-start',
        padding: isMobile ? 0 : isNarrow ? 12 : 20,
        pointerEvents: 'none', // the page behind stays scrollable and clickable
      };

  const fullScreen = wide || isMobile;
  const panelStyle: React.CSSProperties = {
    pointerEvents: 'auto',
    display: 'flex',
    flexDirection: 'column',
    minWidth: 0,
    width: fullScreen ? '100%' : 'min(1440px, 100%)',
    height: fullScreen ? '100%' : 'min(900px, 92dvh)',
    maxWidth: '100%',
    maxHeight: '100%',
    background: token.colorBgContainer,
    border: fullScreen ? undefined : `1px solid ${token.colorBorderSecondary}`,
    borderRadius: fullScreen ? 0 : token.borderRadiusLG,
    boxShadow: fullScreen ? undefined : token.boxShadowSecondary,
    overflow: 'hidden',
    fontFamily: token.fontFamily,
    fontSize: 14,
    color: token.colorText,
  };

  return (
    <div style={scrimStyle}>
      <section role='dialog' aria-label={tr('title', lang)} style={panelStyle}>
        <div
          style={{ flex: 'none', padding: '12px 16px 10px', borderBottom: `1px solid ${token.colorBorderSecondary}` }}
        >
          <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
            <div style={{ flex: 1, fontWeight: 700, fontSize: 16 }}>{tr('title', lang)}</div>
            <div role='group' aria-label='Language' style={{ display: 'inline-flex', gap: 4 }}>
              {(['vi', 'en'] as PlaybookLang[]).map((code) => (
                <Button
                  key={code}
                  size='small'
                  type={lang === code ? 'primary' : 'default'}
                  aria-pressed={lang === code}
                  onClick={() => onLangChange(code)}
                >
                  {code.toUpperCase()}
                </Button>
              ))}
            </div>
            {!isMobile && (
              <Tooltip title={wide ? tr('collapse', lang) : tr('expand', lang)}>
                <Button
                  type='text'
                  aria-label={wide ? tr('collapse', lang) : tr('expand', lang)}
                  icon={wide ? <FullscreenExitOutlined /> : <FullscreenOutlined />}
                  onClick={onToggleWide}
                />
              </Tooltip>
            )}
            <Tooltip title={tr('close', lang)}>
              <Button type='text' aria-label={tr('close', lang)} icon={<CloseOutlined />} onClick={onClose} />
            </Tooltip>
          </div>
          <Input
            ref={searchRef}
            allowClear
            prefix={<SearchOutlined />}
            placeholder={tr('search', lang)}
            value={keyword}
            onChange={(e) => setKeyword(e.target.value)}
            style={{ marginTop: 8 }}
          />
        </div>

        <div
          style={{
            flex: 1,
            display: 'grid',
            gridTemplateColumns: isNarrow ? 'minmax(0, 1fr)' : '320px minmax(0, 1fr)',
            minHeight: 0,
            minWidth: 0,
          }}
        >
          {!isNarrow && (
            <div
              style={{
                borderRight: `1px solid ${token.colorBorderSecondary}`,
                overflowY: 'auto',
                padding: '9px 7px',
                background: token.colorBgLayout,
                minWidth: 0,
              }}
            >
              {navBody}
            </div>
          )}

          <div style={{ display: 'flex', flexDirection: 'column', minWidth: 0, minHeight: 0 }}>
            {isNarrow && roots.length > 0 && (
              <Select
                aria-label={tr('pickSection', lang)}
                value={rootId}
                onChange={openSection}
                style={{ margin: '9px 16px 0', width: 'calc(100% - 32px)' }}
                options={roots.map((r) => ({ value: r.id, label: r.title }))}
              />
            )}

            <div
              style={{
                flex: 'none',
                padding: isNarrow ? '12px 16px 10px' : '14px 24px 12px',
                borderBottom: `1px solid ${token.colorBorderSecondary}`,
              }}
            >
              <Typography.Text
                className='!text-[20px] !font-bold !tracking-tight'
                ellipsis={{ tooltip: searching ? undefined : rootTitle }}
              >
                {searching ? `${results.length} ${tr('results', lang)} — “${kw}”` : rootTitle}
              </Typography.Text>
            </div>

            <div
              ref={bodyRef}
              style={{
                flex: 1,
                overflowY: 'auto',
                background: token.colorBgLayout,
                padding: isNarrow ? '14px 16px 28px' : '20px 28px 36px',
                display: 'flex',
                flexDirection: 'column',
                gap: 12,
              }}
            >
              {renderBody()}
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default PlaybookPanel;
